import { metaRepo } from '../../data/repositories';
import { fromBase64, toBase64 } from './envelope';
import { deriveBackupKey, generatePassphrase, newSalt } from './passphrase';
import type { BackupCredentials } from '../types';

/**
 * The two secrets, and where they live.
 *
 * They are deliberately independent, and that costs a family one extra line on a card:
 *
 *  - the **device token** authenticates to `/api/backup`. The server sees it.
 *  - the **six words** decrypt the blob. The server never sees them, and must not be able to.
 *
 * Deriving one from the other would halve what has to be written down, and it is the wrong trade: the
 * token is a server-side value, so a server compromise plus a token would turn a 48-bit passphrase into
 * an offline guessing problem with a known target. Keeping them separate means a stolen token buys the
 * ability to overwrite backups, never to read one.
 *
 * ── Why this lives in `meta` and not in `settings` ──
 *
 * `meta` holds facts about the **device**; `settings` and the content stores hold facts about the
 * **person**. Only the second kind is exported, so putting the credentials here keeps them out of every
 * backup file by construction rather than by a filter somebody has to remember. It is also why
 * `applyImport` must not clear `meta` — see the comment there.
 */

/** Bytes of device token. 32 bytes, base64url, is 43 characters — long enough to be unguessable. */
const TOKEN_BYTES = 32;

export interface MintedSecrets {
  /** Shown once, then never again. Not stored in plaintext anywhere. */
  passphrase: string;
  token: string;
}

/**
 * Create the credentials and store what has to persist.
 *
 * **The passphrase itself is never stored**, only the salt and the non-extractable key derived from it.
 * That is the whole point: a device that is compromised can seal new backups but cannot tell an attacker
 * the words needed to read old ones, and nothing in the database can be turned back into them.
 *
 * The token *is* stored, because the device has to keep authenticating without anybody present.
 */
export async function mintBackupSecrets(): Promise<MintedSecrets> {
  const passphrase = generatePassphrase();
  const token = toBase64url(crypto.getRandomValues(new Uint8Array(TOKEN_BYTES)));
  const salt = newSalt();
  const key = await deriveBackupKey(passphrase, salt);

  const credentials: BackupCredentials = {
    token,
    salt: toBase64(salt),
    key,
    createdAt: new Date().toISOString(),
    acknowledgedAt: null,
  };

  await metaRepo.update({ backup: credentials });

  return { passphrase, token };
}

export async function backupCredentials(): Promise<BackupCredentials | null> {
  return (await metaRepo.get()).backup ?? null;
}

/**
 * Take on a device key typed off the printed card — the replacement-device path (C57).
 *
 * Without this, the scenario the whole phase exists for could not complete: a fresh install has no `meta`
 * entry, so `isConfigured()` was false and the restore panel offered a caregiver nothing but a dead end.
 * Minting instead was not a workaround but a trap — it generates a *new* random token that Azure will reject,
 * while discarding the lineage the old six words still open.
 *
 * The credential is deliberately incomplete afterwards: a token is enough to list and download backups, and
 * nothing more is knowable until somebody types the six words. Sealing waits for `completeCredentials`.
 */
export async function adoptDeviceToken(token: string): Promise<void> {
  const trimmed = token.trim();
  if (trimmed === '') throw new Error('adoptDeviceToken: empty token');

  const existing = await backupCredentials();
  await metaRepo.update({
    backup: {
      ...existing,
      token: trimmed,
      createdAt: existing?.createdAt ?? new Date().toISOString(),
      // Nobody has written anything down on this device — the card came from the old one.
      acknowledgedAt: existing?.acknowledgedAt ?? null,
    },
  });
}

/**
 * Fill in the sealing material after a restore, so the device carries on backing itself up.
 *
 * Called from `restore.ts` on every successful **sealed** restore, which makes the lineage continue rather
 * than restart: same passphrase, same salt, same cloud history. The replacement device is not a new device as
 * far as the backups are concerned, and the card in the drawer stays correct.
 *
 * Does nothing without a token, because there would be nowhere to push to.
 */
export async function completeCredentials(args: {
  salt: string;
  passphrase: string;
}): Promise<void> {
  const existing = await backupCredentials();
  if (!existing) return;

  const key = await deriveBackupKey(args.passphrase, fromBase64(args.salt));
  await metaRepo.update({ backup: { ...existing, salt: args.salt, key } });
}

/** Whether this device can seal a backup, as opposed to merely read one. */
export function canSeal(credentials: BackupCredentials | null): boolean {
  return Boolean(credentials?.key && credentials.salt);
}

/**
 * Record that a human has written the secrets down.
 *
 * Kept as a timestamp rather than a boolean because the useful question later is not "did somebody tick
 * a box" but "when, and was that before or after the device changed hands".
 */
export async function acknowledgeBackupSecrets(): Promise<void> {
  const existing = await backupCredentials();
  if (!existing) return;
  await metaRepo.update({ backup: { ...existing, acknowledgedAt: new Date().toISOString() } });
}

function toBase64url(bytes: Uint8Array): string {
  return toBase64(bytes).replace(/\+/gu, '-').replace(/\//gu, '_').replace(/=+$/u, '');
}
